import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  MenuItem,
  Grid,
} from '@mui/material';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import moment from 'moment';

interface AddCredentialProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  editData?: any;
}

const credentialTypes = ['SPN Client Secret', 'Certificate', 'API Key', 'Token', 'Other'];
const environments = ['DEV', 'QA', 'PROD', 'PREPROD', 'OTHER'];

export default function AddCredentialModal({
  open,
  onClose,
  onSave,
  editData,
}: AddCredentialProps) {
  const [type, setType] = useState(editData?.type || '');
  const [name, setName] = useState(editData?.name || '');
  const [identifier, setIdentifier] = useState(editData?.identifier || '');
  const [owner, setOwner] = useState(editData?.owner || '');
  const [environment, setEnvironment] = useState(editData?.environment || '');

  const [expiryDate, setExpiryDate] = useState<any>(
    editData?.expiryDate ? moment(editData.expiryDate) : null,
  );

  const [creationDate, setCreationDate] = useState<any>(
    editData?.creationDate ? moment(editData.creationDate) : moment(),
  );

  const [lastRenewalDate, setLastRenewalDate] = useState<any>(
    editData?.lastRenewalDate ? moment(editData.lastRenewalDate) : null,
  );

  const [renewalContact, setRenewalContact] = useState(editData?.renewalContact?.[0] || '');

  const [notes, setNotes] = useState(editData?.notes || '');

  const handleSave = () => {
    const payload = {
      type,
      name,
      identifier,
      owner,
      environment,
      expiryDate: expiryDate ? moment(expiryDate).toISOString() : null,
      creationDate: creationDate ? moment(creationDate).toISOString() : null,
      lastRenewalDate: lastRenewalDate ? moment(lastRenewalDate).toISOString() : null,
      renewalContact: [renewalContact],
      notes,
    };

    onSave(payload);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{editData ? 'Edit Credential' : 'Add New Credential'}</DialogTitle>

        <DialogContent>
          <Grid container spacing={2} mt={1}>
            {/* TYPE */}
            <Grid item xs={12}>
              <TextField
                select
                label="Credential Type"
                fullWidth
                required
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {credentialTypes.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* NAME */}
            <Grid item xs={12}>
              <TextField
                label="Credential Name"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>

            {/* IDENTIFIER */}
            <Grid item xs={12}>
              <TextField
                label="Identifier"
                fullWidth
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Unique ID e.g. SPN-NAME-123"
              />
            </Grid>

            {/* OWNER */}
            <Grid item xs={12}>
              <TextField
                label="Owner"
                fullWidth
                required
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
              />
            </Grid>

            {/* ENVIRONMENT */}
            <Grid item xs={12}>
              <TextField
                select
                label="Environment"
                fullWidth
                required
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
              >
                {environments.map((env) => (
                  <MenuItem key={env} value={env}>
                    {env}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* EXPIRY DATE */}
            <Grid item xs={12}>
              <DatePicker
                label="Expiry Date"
                value={expiryDate}
                onChange={(newDate) => setExpiryDate(newDate ? moment(newDate) : null)}
                renderInput={(params) => <TextField required fullWidth {...params} />}
              />
            </Grid>

            {/* CREATION DATE */}
            <Grid item xs={12}>
              <DatePicker
                label="Creation Date"
                value={creationDate}
                onChange={(newDate) => setCreationDate(newDate ? moment(newDate) : null)}
                renderInput={(params) => <TextField fullWidth {...params} disabled={!!editData} />}
              />
            </Grid>

            {/* LAST RENEWAL DATE */}
            <Grid item xs={12}>
              <DatePicker
                label="Last Renewal Date"
                value={lastRenewalDate}
                onChange={(newDate) => setLastRenewalDate(newDate ? moment(newDate) : null)}
                renderInput={(params) => <TextField fullWidth {...params} />}
              />
            </Grid>

            {/* RENEWAL CONTACT */}
            <Grid item xs={12}>
              <TextField
                label="Renewal Contact"
                fullWidth
                required
                value={renewalContact}
                onChange={(e) => setRenewalContact(e.target.value)}
              />
            </Grid>

            {/* NOTES */}
            <Grid item xs={12}>
              <TextField
                label="Renewal Instructions/Link"
                fullWidth
                required
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>

            {/* ACTION BUTTONS */}
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="contained" onClick={handleSave}>
                Save
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  );
}
